import { useId } from 'react'
import { useRouter } from 'next/router'
import useTranslation from 'next-translate/useTranslation'
import clsx from 'clsx'
import CalendarIcon from '@ancon/wildcat-ui/shared/icons/calendar-event-outline.svg'
import AddIcon from '@ancon/wildcat-ui/shared/icons/add-circle.svg'

import useAppSelector from '../../../store/hooks/useAppSelector'
import useRadioGroupKeyboard from '../../app/hooks/useRadioGroupKeyboard'
import { clientContextIsCompanyUserSelector } from '../../clientContext/store/clientContextSelectors'
import useProfileOptions from '../hooks/useProfileOptions'
import useSwitchProfile from '../hooks/useSwitchProfile'
import { CompanyRoutes, PrivateProfileId } from '../constants'

import ProfileAvatar from './ProfileAvatar'
import styles from './ProfileSwitcherOptions.module.scss'

type ProfileSwitcherOptionsProps = {
  /** Runs after a choice is made, e.g. to close the popover */
  onDone?: () => void
  /** Leaves out the company links, e.g. where they are already on screen */
  hideCompanyLinks?: boolean
}

/**
 * "Ordering for": the profiles to switch between and, for company profiles,
 * the company overview and a new company order
 */
export default function ProfileSwitcherOptions({
  onDone,
  hideCompanyLinks = false,
}: ProfileSwitcherOptionsProps) {
  const { t } = useTranslation('common')
  const router = useRouter()
  const labelId = useId()

  const profileOptions = useProfileOptions()
  const isCompanyUser = useAppSelector(clientContextIsCompanyUserSelector)
  const { switchToCompanyProfile, switchToPrivateProfile, pendingProfileId } =
    useSwitchProfile()
  // Arrows only move focus: checking a profile switches to it
  const { getTabIndex, onKeyDown } = useRadioGroupKeyboard(
    profileOptions.findIndex(option => option.isCurrent),
  )

  async function handleSelectProfile(id: string, isVerified: boolean) {
    const switched =
      id === PrivateProfileId
        ? await switchToPrivateProfile()
        : await switchToCompanyProfile(id, isVerified)

    if (switched) {
      onDone?.()
    }
  }

  function handleNavigate(pathname: string) {
    onDone?.()
    router.push(pathname)
  }

  return (
    <div className={styles.container}>
      <div id={labelId} className={styles.label}>
        {t('identity.orderingFor')}
      </div>
      <div role="radiogroup" aria-labelledby={labelId}>
        {profileOptions.map((option, index) => (
          <button
            key={option.id}
            type="button"
            role="radio"
            aria-checked={option.isCurrent}
            tabIndex={getTabIndex(index)}
            onKeyDown={onKeyDown}
            className={clsx(styles.option, {
              [styles.current]: option.isCurrent,
            })}
            disabled={!!pendingProfileId}
            onClick={() =>
              option.isCurrent
                ? onDone?.()
                : handleSelectProfile(option.id, option.isVerified)
            }
          >
            <ProfileAvatar
              name={option.avatarName}
              isCompany={option.isCompany}
              size="small"
            />
            <span className={styles.optionText}>
              <span className={styles.optionName}>{option.name}</span>
              <span className={styles.optionHint}>{option.hint}</span>
            </span>
            <span className={styles.radio} aria-hidden />
          </button>
        ))}
      </div>
      {isCompanyUser && !hideCompanyLinks && (
        <div className={styles.links}>
          <button
            type="button"
            className={styles.link}
            onClick={() => handleNavigate(CompanyRoutes.Overview)}
          >
            <CalendarIcon aria-hidden />
            {t('identity.companyOverview')}
          </button>
          <button
            type="button"
            className={styles.link}
            onClick={() => handleNavigate(CompanyRoutes.NewOrder)}
          >
            <AddIcon aria-hidden />
            {t('identity.newCompanyOrder')}
          </button>
        </div>
      )}
    </div>
  )
}
